import { useRouter } from 'next/router';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import FavoritePresenter from 'lib/Presenters/FavoritePresenter';
import { colors, screen } from 'components/styles/variables';
import EmptyPageCard, {
  EmptyPageCardType,
} from 'components/Favorites/FavoriteStateCards/EmptyPageCard';
import { useSelector, useDispatch } from 'react-redux';
import State, { RequestStates } from 'types/State';
import { ReactNode } from 'react';
import css from 'styled-jsx/css';
import { removeFavorite } from 'redux-implementations/favorites';
import ReturnToLastSearch from './ReturnToLastSearch';
import SignInCard from './FavoriteStateCards/SignInCard';
import LoadingFavoriteCard from './LoadingFavoriteCard';
import ActiveFavoriteCard from './ActiveFavoriteCard';
import InactiveFavoriteCard from './InactiveFavoriteCard';

const loading = css.resolve`
  .loading-card {
    margin-bottom: 16px;
  }
`;

function FavoriteCards({ favorites }: { favorites: FavoritePresenter[] }): JSX.Element {
  const dispatch = useDispatch();
  return (
    <TransitionGroup component={null}>
      {favorites.map((favorite): JSX.Element => {
        const onFavoriteRemoval = (): void => {
          dispatch(removeFavorite(favorite.vin));
        };
        const favoriteCardProps = {
          ...favorite.toJSON(),
          removeFavorite: onFavoriteRemoval,
        };

        return (
          <CSSTransition key={favorite.vdpUrl} timeout={{ exit: 500 }}>
            {favorite.active ? (
              <ActiveFavoriteCard {...favoriteCardProps} />
            ) : (
              <InactiveFavoriteCard {...favoriteCardProps} />
            )}
          </CSSTransition>
        );
      })}
    </TransitionGroup>
  );
}

const Favorites = ({
  favorites,
  isFavoritesActive,
  requestState,
}: {
  favorites: FavoritePresenter[];
  isFavoritesActive: boolean;
  requestState: RequestStates;
}): JSX.Element => {
  const isSignedIn = useSelector<State>((state) => state?.session?.isSignedIn);
  const isFirebaseLoading = typeof isSignedIn === 'undefined';
  const emptyCardType = isFavoritesActive
    ? EmptyPageCardType.favorites
    : EmptyPageCardType.priceDrops;

  let cards: ReactNode;

  if (isFirebaseLoading || [RequestStates.Failed, RequestStates.Requested].includes(requestState)) {
    cards = [0, 1, 2].map((el) => (
      <LoadingFavoriteCard key={el} className={`loading-card ${loading.className}`} />
    ));
  } else if (isSignedIn === false) {
    cards = <SignInCard />;
  } else if (isSignedIn && favorites.length > 0) {
    cards = <FavoriteCards favorites={favorites} />;
  } else if (isSignedIn && favorites.length === 0) {
    cards = <EmptyPageCard cardType={emptyCardType} />;
  }
  const router = useRouter();

  return (
    <section className="favorites-section">
      <div className="favorites-layout">
        <div className="cards">{cards}</div>
        <div className="right-rail">
          <ReturnToLastSearch
            href={'/listings'}
            onClick={(e): void => {
              e.preventDefault();
              router.back();
            }}
          />
        </div>
      </div>
      <style jsx>{`
        .favorites-section {
          background-color: ${colors.G1};
          padding: 18px 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        @media screen and (min-width: ${screen.smMin}) {
          .favorites-section {
            min-height: 800px;
          }
        }

        .cards {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        @media screen and (min-width: ${screen.mdMin}) {
          .cards {
            width: 770px;
            min-height: 600px;
          }
        }

        @media screen and (min-width: ${screen.lgMin}) {
          .cards {
            margin-right: 24px;
          }
        }

        .page-card {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        @media screen and (min-width: ${screen.mdMin}) {
          .page-card {
            width: 770px;
          }
        }

        @media screen and (min-width: ${screen.lgMin}) {
          .page-card {
            margin-right: 24px;
          }
        }

        .favorites-layout {
          width: 100%;
        }

        @media screen and (min-width: ${screen.mdMin}) {
          .favorites-layout {
            width: initial;
          }
        }

        @media screen and (min-width: ${screen.lgMin}) {
          .favorites-layout {
            display: flex;
            justify-content: space-between;
          }
        }

        .right-rail {
          display: none;
        }

        @media screen and (min-width: ${screen.lgMin}) {
          .right-rail {
            display: block;
          }
        }
      `}</style>
      {loading.styles}
    </section>
  );
};

export default Favorites;
